import { create } from 'zustand';
import { apiClient } from '../lib/api';

export interface AgentTeamMembership {
  teamId: string;
  teamUuid?: string;
  teamName: string;
  teamKey: string;
  teamRoleCode: string;
  teamRoleName: string;
  isPrimary: boolean;
}

export interface AgentProfile {
  id: string;
  kind: 'internal' | 'external';
  userId: string;
  employeeId?: string;
  supplierOrgId?: string;
  supplierOrgName?: string;
  accessLevel: 'admin' | 'member' | 'external';
  status: 'invited' | 'active' | 'suspended' | 'inactive';
  skills: string[];
  maxOpenTickets: number;
  availabilityStatus: string;
  outOfOfficeUntil?: string;
  delegateProfileId?: string;
  defaultCalendarId?: string;
  accessExpiresAt?: string;
  lastActiveAt?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  department?: string;
  designation?: string;
  managerName?: string;
  isEffective: boolean;
  warningFlag?: string;
  teams: AgentTeamMembership[];
  createdAt: string;
  updatedAt: string;
}

export interface EligibleEmployee {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  workEmail: string;
  phone?: string;
  department?: string;
  designation?: string;
  status: string;
  userId?: string;
  hasLogin: boolean;
  accessLevel?: 'admin' | 'member';
}

export interface SupplierOrg {
  id: string;
  name: string;
  domain?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  moneySupplierId?: string;
  isActive: boolean;
  createdAt: string;
}

export interface SupportPermission {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  scope: 'global' | 'team';
  granted?: boolean;
}

export interface TeamRoleDef {
  id: string;
  code: string;
  name: string;
  description?: string;
  isSystem: boolean;
  permissions: SupportPermission[];
}

export interface AccessAuditLog {
  id: string;
  actorId?: string;
  actorName?: string;
  targetProfileId: string;
  action: string;
  oldState?: any;
  newState?: any;
  reason?: string;
  createdAt: string;
}

export interface AccessMatrixCell {
  agentProfileId: string;
  agentName: string;
  teamId: string;
  teamName: string;
  teamRoleCode: string;
  teamRoleName: string;
  isPrimary: boolean;
}

export interface CurrentAgentContext {
  profile: AgentProfile | null;
  teams: any[];
  permissions: string[];
  isSuperAdmin: boolean;
}

interface AgentManagementState {
  agents: AgentProfile[];
  eligibleEmployees: EligibleEmployee[];
  supplierOrgs: SupplierOrg[];
  roles: TeamRoleDef[];
  accessMatrix: {
    agents: Array<{ id: string; name: string; email: string; accessLevel: string }>;
    teams: Array<{ id: string; name: string; type: string }>;
    matrix: AccessMatrixCell[];
  } | null;
  auditLogs: AccessAuditLog[];
  currentAgent: CurrentAgentContext | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAgents: (params?: { kind?: string; status?: string; teamId?: string; search?: string }) => Promise<void>;
  fetchEligibleEmployees: (search?: string) => Promise<void>;
  fetchSupplierOrgs: () => Promise<void>;
  fetchRoles: () => Promise<void>;
  fetchAccessMatrix: () => Promise<void>;
  fetchAuditLogs: (agentId?: string) => Promise<void>;
  fetchCurrentAgent: () => Promise<void>;

  createInternalAgent: (data: {
    employeeId: string;
    accessLevel: 'admin' | 'member';
    teams: Array<{ teamId: string; teamRoleCode: string; isPrimary?: boolean }>;
    skills?: string[];
    maxOpenTickets?: number;
    calendarId?: string;
  }) => Promise<AgentProfile>;

  createExternalAgent: (data: {
    supplierOrgId: string;
    name: string;
    email: string;
    phone?: string;
    teams: Array<{ teamId: string; teamRoleCode?: string }>;
    accessExpiresAt?: string;
  }) => Promise<AgentProfile>;

  createSupplierOrg: (data: {
    name: string;
    domain?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    moneySupplierId?: string;
  }) => Promise<SupplierOrg>;

  updateAgent: (id: string, updates: Partial<AgentProfile>) => Promise<void>;
  setAgentTeams: (id: string, teams: Array<{ teamId: string; teamRoleCode: string; isPrimary?: boolean }>) => Promise<void>;
  suspendAgent: (id: string, reason: string) => Promise<void>;
  reactivateAgent: (id: string) => Promise<void>;
  deactivateAgent: (id: string, reason: string, reassign: { mode: 'queue' | 'user'; userId?: string }) => Promise<void>;
  resendInvite: (id: string) => Promise<void>;
  updateExpiry: (id: string, accessExpiresAt: string) => Promise<void>;
  updateRolePermissions: (roleId: string, permissionCodes: string[]) => Promise<void>;
}

function normalizeAgent(a: any): AgentProfile {
  const rawTeams = Array.isArray(a.teams) ? a.teams : [];
  const teamMap = new Map<string, AgentTeamMembership>();

  for (const t of rawTeams) {
    const teamUuid = t.teamUuid || t.uuid || (typeof t.teamId === 'string' && t.teamId.includes('-') ? t.teamId : '');
    const teamNumericId = t.teamNumericId || (typeof t.teamId === 'number' || (!isNaN(Number(t.teamId)) && !String(t.teamId).includes('-')) ? String(t.teamId) : '');
    const teamId = String(teamUuid || t.teamId || t.team_id || t.id || teamNumericId || '');
    const teamKey = String(t.teamKey || t.team_key || t.teamCode || t.team_code || t.code || '');
    const teamName = String(t.teamName || t.team_name || t.name || '').replace(/\bDesk\b/gi, 'Team');

    // Key by teamKey lowercase (e.g. 'sd', 'hr', 'sw', 'mgt', 'sup', 'adm') or UUID or ID
    const dedupKey = (teamKey ? teamKey.toLowerCase() : (teamUuid || teamNumericId || teamName.toLowerCase())).trim();
    if (!dedupKey) continue;

    const rawRoleCode = String(t.teamRoleCode || t.team_role_code || t.roleCode || t.role_code || 'agent').toLowerCase();
    const rawRoleName = t.teamRoleName || t.team_role_name || t.roleName || t.role_name || (
      rawRoleCode === 'team_lead' ? 'Team Lead' : rawRoleCode === 'team_manager' ? 'Team Manager' : 'Support Agent'
    );
    const isPrimary = Boolean(t.isPrimary ?? t.is_primary ?? false);

    const existing = teamMap.get(dedupKey);
    if (!existing) {
      teamMap.set(dedupKey, {
        teamId,
        teamUuid: teamUuid || undefined,
        teamName,
        teamKey,
        teamRoleCode: rawRoleCode,
        teamRoleName: rawRoleName,
        isPrimary,
      });
    } else if (!existing.isPrimary && isPrimary) {
      existing.isPrimary = true;
    }
  }

  const teams = Array.from(teamMap.values());
  if (teams.length > 0 && !teams.some((t) => t.isPrimary)) {
    teams[0].isPrimary = true;
  }

  return {
    id: String(a.id || a.profile_id || a.uuid || ''),
    kind: a.kind || 'internal',
    userId: String(a.userId || a.user_id || a.user_uuid || ''),
    employeeId: a.employeeId || a.employee_id || a.employee_uuid || undefined,
    supplierOrgId: a.supplierOrgId ? String(a.supplierOrgId) : (a.supplier_org_id ? String(a.supplier_org_id) : undefined),
    supplierOrgName: a.supplierOrgName || a.supplier_org_name || undefined,
    accessLevel: a.accessLevel || a.access_level || 'member',
    status: a.status || 'active',
    skills: Array.isArray(a.skills) ? a.skills : [],
    maxOpenTickets: Number(a.maxOpenTickets ?? a.max_open_tickets ?? 10),
    availabilityStatus: a.availabilityStatus || a.availability_status || 'available',
    outOfOfficeUntil: a.outOfOfficeUntil || a.out_of_office_until || undefined,
    delegateProfileId: a.delegateProfileId || a.delegate_profile_id || undefined,
    defaultCalendarId: a.defaultCalendarId || a.default_calendar_id || undefined,
    accessExpiresAt: a.accessExpiresAt || a.access_expires_at || undefined,
    lastActiveAt: a.lastActiveAt || a.last_active_at || undefined,
    firstName: a.firstName || a.first_name || '',
    lastName: a.lastName || a.last_name || '',
    email: a.email || a.user_email || '',
    phone: a.phone || a.contact_phone || a.phone_number || undefined,
    department: a.department || a.department_name || undefined,
    designation: a.designation || a.designation_title || undefined,
    managerName: a.managerName || a.manager_name || undefined,
    isEffective: Boolean(a.isEffective ?? a.is_effective ?? true),
    warningFlag: a.warningFlag || a.warning_flag || undefined,
    teams,
    createdAt: a.createdAt || a.created_at || '',
    updatedAt: a.updatedAt || a.updated_at || '',
  };
}

function normalizeEmployee(e: any): EligibleEmployee {
  return {
    id: String(e.id || e.employee_id || ''),
    employeeNumber: e.employeeNumber || e.employee_code || '',
    firstName: e.firstName || e.first_name || '',
    lastName: e.lastName || e.last_name || '',
    workEmail: e.workEmail || e.email || '',
    phone: e.phone || undefined,
    department: e.department || undefined,
    designation: e.designation || undefined,
    status: e.status || 'active',
    userId: e.userId || e.user_id || undefined,
    hasLogin: Boolean(e.hasLogin ?? e.has_login ?? false),
    accessLevel: (e.accessLevel || e.access_level || 'member') as 'admin' | 'member',
  };
}

function normalizeSupplierOrg(s: any): SupplierOrg {
  return {
    id: String(s.id || ''),
    name: s.name || '',
    domain: s.domain || undefined,
    contactName: s.contactName || s.contact_name || undefined,
    contactEmail: s.contactEmail || s.contact_email || undefined,
    contactPhone: s.contactPhone || s.contact_phone || undefined,
    moneySupplierId: s.moneySupplierId || s.money_supplier_id || undefined,
    isActive: Boolean(s.isActive ?? s.is_active ?? true),
    createdAt: s.createdAt || s.created_at || '',
  };
}

export const useAgentManagementStore = create<AgentManagementState>((set, get) => ({
  agents: [],
  eligibleEmployees: [],
  supplierOrgs: [],
  roles: [],
  accessMatrix: null,
  auditLogs: [],
  currentAgent: null,
  isLoading: false,
  error: null,

  fetchAgents: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/support/agents', { params });
      const raw = res.data?.data ?? res.data;
      const rawList = Array.isArray(raw) ? raw : (raw?.items ?? raw?.data ?? []);
      const agents = rawList.map(normalizeAgent);
      set({ agents, isLoading: false });
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message, isLoading: false });
    }
  },

  fetchEligibleEmployees: async (search) => {
    try {
      const res = await apiClient.get('/support/agents/eligible-employees', { params: { q: search } });
      const raw = res.data?.data ?? res.data;
      const rawList = Array.isArray(raw) ? raw : [];
      set({ eligibleEmployees: rawList.map(normalizeEmployee) });
    } catch (err: any) {
      console.warn('Failed to fetch eligible employees:', err);
    }
  },

  fetchSupplierOrgs: async () => {
    try {
      const res = await apiClient.get('/support/supplier-orgs');
      const raw = res.data?.data ?? res.data;
      const rawList = Array.isArray(raw) ? raw : [];
      set({ supplierOrgs: rawList.map(normalizeSupplierOrg) });
    } catch (err: any) {
      console.warn('Failed to fetch supplier orgs:', err);
    }
  },

  fetchRoles: async () => {
    try {
      const res = await apiClient.get('/support/roles');
      const raw = res.data?.data ?? res.data;
      const rolesList = Array.isArray(raw) ? raw : (raw?.roles || []);
      set({ roles: rolesList });
    } catch (err: any) {
      console.warn('Failed to fetch roles:', err);
    }
  },

  fetchAccessMatrix: async () => {
    try {
      const res = await apiClient.get('/support/access/matrix');
      const raw = res.data?.data ?? res.data;
      if (raw) {
        if (Array.isArray(raw.teams)) {
          raw.teams = raw.teams.map((t: any) => ({
            ...t,
            name: String(t.name || '').replace(/\bDesk\b/gi, 'Team'),
          }));
        }
        if (Array.isArray(raw.matrix)) {
          raw.matrix = raw.matrix.map((m: any) => ({
            ...m,
            teamName: String(m.teamName || '').replace(/\bDesk\b/gi, 'Team'),
          }));
        }
      }
      set({ accessMatrix: raw });
    } catch (err: any) {
      console.warn('Failed to fetch access matrix:', err);
    }
  },

  fetchAuditLogs: async (agentId) => {
    try {
      const res = await apiClient.get('/support/access/audit', { params: { agentId } });
      const raw = res.data?.data ?? res.data;
      set({ auditLogs: Array.isArray(raw) ? raw : [] });
    } catch (err: any) {
      console.warn('Failed to fetch audit logs:', err);
    }
  },

  fetchCurrentAgent: async () => {
    try {
      const res = await apiClient.get('/support/agents/me');
      const raw = res.data?.data ?? res.data;
      set({ currentAgent: raw });
    } catch (err: any) {
      console.warn('Failed to fetch current agent permissions:', err);
    }
  },

  createInternalAgent: async (data) => {
    const res = await apiClient.post('/support/agents', data);
    const created = res.data?.data ?? res.data;
    await get().fetchAgents();
    return created;
  },

  createExternalAgent: async (data) => {
    const res = await apiClient.post('/support/agents/external', data);
    const created = res.data?.data ?? res.data;
    await get().fetchAgents();
    return created;
  },

  createSupplierOrg: async (data) => {
    const res = await apiClient.post('/support/supplier-orgs', data);
    const created = res.data?.data ?? res.data;
    await get().fetchSupplierOrgs();
    return created;
  },

  updateAgent: async (id, updates) => {
    await apiClient.patch(`/support/agents/${id}`, updates);
    await get().fetchAgents();
  },

  setAgentTeams: async (id, teams) => {
    await apiClient.put(`/support/agents/${id}/teams`, { teams });
    await get().fetchAgents();
  },

  suspendAgent: async (id, reason) => {
    await apiClient.post(`/support/agents/${id}/suspend`, { reason });
    await get().fetchAgents();
  },

  reactivateAgent: async (id) => {
    await apiClient.post(`/support/agents/${id}/reactivate`);
    await get().fetchAgents();
  },

  deactivateAgent: async (id, reason, reassign) => {
    await apiClient.post(`/support/agents/${id}/deactivate`, { reason, reassign });
    await get().fetchAgents();
  },

  resendInvite: async (id) => {
    await apiClient.post(`/support/agents/${id}/resend-invite`);
  },

  updateExpiry: async (id, accessExpiresAt) => {
    await apiClient.patch(`/support/agents/${id}/expiry`, { accessExpiresAt });
    await get().fetchAgents();
  },

  updateRolePermissions: async (roleId, permissionCodes) => {
    await apiClient.put(`/support/roles/${roleId}/permissions`, {
      permissions: permissionCodes,
      permissionCodes,
    });
    await get().fetchRoles();
  },
}));
